import React, { useState } from 'react';
import Navbar from '../components/Navbar';

const PromptLife2 = () => {
  const [name, setName] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showTraits, setShowTraits] = useState(false);
  const [showBackstory, setShowBackstory] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [characterTraits, setCharacterTraits] = useState(null);
  const [storyData, setStoryData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleStart = () => {
    if (name.trim()) {
      setShowSettings(true);
    }
  };

  const handleNext = () => {
    if (selectedSetting) {
      // Generate random traits
      const traits = {
        financial: ['rich', 'middle class', 'poor'][Math.floor(Math.random() * 3)],
        luck: ['lucky', 'normal', 'unlucky'][Math.floor(Math.random() * 3)],
        looks: ['pretty', 'average'][Math.floor(Math.random() * 2)]
      };
      setCharacterTraits(traits);
      setShowTraits(true);
    }
  };

  const handleBegin = async () => {
    if (characterTraits && selectedSetting) {
      setIsLoading(true);
      
      try {
        const selectedSettingText = storySettings.find(s => s.id === selectedSetting)?.text;
        
        const prompt = `Generate a creative and engaging backstory for a character named ${name} in the setting of ${selectedSettingText}. 

Character traits:
- Financial situation: ${characterTraits.financial}
- Luck: ${characterTraits.luck}
- Looks: ${characterTraits.looks}

Write a detailed backstory describing ${name}'s life in this world from birth until they are 15 years old. The story should be several paragraphs long and incorporate how their traits influenced their early life experiences. Make it engaging and immersive, showing how the setting and traits shaped their personality and circumstances.

At age 15, ${name} faces a major life-changing event. This event should be dramatic and force ${name} to make a difficult decision that will shape their future. The event should be related to their traits and the setting they're in.`;

        const response = await fetch('/.netlify/functions/gemini', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ prompt }),
        });

        if (response.ok) {
          const data = await response.json();
          try {
            // Parse the JSON response from Gemini
            const storyResponse = JSON.parse(data.response);
            setStoryData(storyResponse);
            setShowBackstory(true);
          } catch (parseError) {
            console.error('Error parsing Gemini response:', parseError);
            // Fallback to plain text if JSON parsing fails
            setStoryData({
              backstory: data.response,
              event: 'A significant event occurs in your life',
              choice1: 'Make a bold decision',
              choice2: 'Take a cautious approach',
              choice3: 'Wait and observe'
            });
            setShowBackstory(true);
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('API Error:', response.status, errorData);
          throw new Error(`Failed to generate backstory: ${response.status} ${errorData.error || ''}`);
        }
      } catch (error) {
        console.error('Error generating backstory:', error);
        setStoryData({
          backstory: 'Sorry, there was an error generating your backstory. Please try again.',
          event: 'An error occurred',
          choice1: 'Try again',
          choice2: 'Check your connection',
          choice3: 'Contact support'
        });
        setShowBackstory(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleNextToChoices = () => {
    setShowChoices(true);
  };

  const storySettings = [
    { id: 1, emoji: '🎨', text: 'Post WW2', explanation: "Where the character is trying to navigate the broken world of society and malfunctioning economy."},
    { id: 2, emoji: '🏡', text: 'Present (2020s)', explanation: "Where the player is trying to dodge the AI takeover over the internet and the possibility of world war 3 or another pandemic." },
    { id: 3, emoji: '🌆', text: 'Cyberpunk City', explanation: "This has nothing to do with cyberpunk 2077. This is a separate world. Just imagine what the world of future would look like. With AI and modifications to the human body." }
  ];

  const traitEmojis = {
    financial: { rich: '💰', 'middle class': '💳', poor: '🪙' },
    luck: { lucky: '🍀', normal: '🎯', unlucky: '☘️' },
    looks: { pretty: '✨', average: '👤' }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Navbar */}
      <div className="md:hidden">
        <Navbar dark={false} mobile={true} />
      </div>
      
      {/* Desktop Navbar */}
      <div className="hidden md:block">
        <Navbar dark={false} />
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        {!showSettings ? (
          // name
          <div className="w-full max-w-xs space-y-4">
            <p className="text-center text-gray-700 text-lg font-medium">
              Choose the name you want to start your life with
            </p>
            
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 text-gray-700 placeholder-gray-400"
              placeholder="Enter your name..."
            />
            
            <button 
              onClick={handleStart}
              className="w-full px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium"
            >
              Next
            </button>
          </div>
        ) : !showTraits ? (
          // Story setting
          <div className="w-full max-w-2xl space-y-6">
            <p className="text-center text-gray-700 text-xl font-medium">
              Select your story setting
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {storySettings.map((setting) => (
                <div
                  key={setting.id}
                  onClick={() => setSelectedSetting(setting.id)}
                  className={`
                    aspect-square bg-white border-2 rounded-lg cursor-pointer transition-all duration-200 flex flex-col items-center justify-center p-4
                    ${selectedSetting === setting.id 
                      ? 'border-black shadow-lg' 
                      : selectedSetting !== null 
                        ? 'border-gray-600 hover:border-gray-400 opacity-50' 
                        : 'border-gray-600 hover:border-gray-400'
                    }
                  `}
                >
                  <div className="text-6xl mb-3">{setting.emoji}</div>
                  <p className="text-black text-center font-medium">{setting.text}</p>
                </div>
              ))}
            </div>

            {/* Next Button */}
            {selectedSetting && (
              <div className="flex justify-center">
                <button 
                  onClick={handleNext}
                  className="px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        ) : !showBackstory ? (
          // Character Traits
          <div className="w-full max-w-2xl space-y-6">
            <p className="text-center text-gray-700 text-xl font-medium">
              Here are your character's traits 🎰
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="aspect-square bg-white border-2 border-gray-600 rounded-lg flex flex-col items-center justify-center p-4">
                <p className="text-sm text-gray-600 mb-2">Finance</p>
                <div className="text-6xl mb-3">{traitEmojis.financial[characterTraits.financial]}</div>
                <p className="text-black text-center font-medium capitalize">{characterTraits.financial}</p>
              </div>
              
              <div className="aspect-square bg-white border-2 border-gray-600 rounded-lg flex flex-col items-center justify-center p-4">
                <p className="text-sm text-gray-600 mb-2">Luck</p>
                <div className="text-6xl mb-3">{traitEmojis.luck[characterTraits.luck]}</div>
                <p className="text-black text-center font-medium capitalize">{characterTraits.luck}</p>
              </div>
              
              <div className="aspect-square bg-white border-2 border-gray-600 rounded-lg flex flex-col items-center justify-center p-4">
                <p className="text-sm text-gray-600 mb-2">Looks</p>
                <div className="text-6xl mb-3">{traitEmojis.looks[characterTraits.looks]}</div>
                <p className="text-black text-center font-medium capitalize">{characterTraits.looks}</p>
              </div>
            </div>

            {/* Begin game */}
            <div className="flex justify-center">
              <button 
                onClick={handleBegin}
                disabled={isLoading}
                className="px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Generating...' : 'Begin'}
              </button>
            </div>
          </div>
        ) : !showChoices ? (
          // Character Backstory
          <div className="w-full max-w-4xl space-y-6">
            <h1 className="text-center text-3xl font-bold text-gray-800 mb-8">
              {name}'s Story
            </h1>
            
            <div className="bg-white border-2 border-gray-300 rounded-lg p-8 shadow-lg">
              <div className="prose prose-lg max-w-none">
                {storyData?.backstory?.split('\n').map((paragraph, index) => (
                  <p key={index} className="text-gray-700 mb-4 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Next Button */}
            <div className="flex justify-center">
              <button 
                onClick={handleNextToChoices}
                className="px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium"
              >
                Next
              </button>
            </div>
          </div>
        ) : (
          // Character Choices
          <div className="w-full max-w-4xl space-y-6">
            <h1 className="text-center text-3xl font-bold text-gray-800 mb-8">
              {name}'s Decision
            </h1>
            
            {/* Event Description */}
            <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-lg">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">The Situation</h2>
              <p className="text-gray-700 leading-relaxed">
                {storyData?.event}
              </p>
            </div>

            {/* Three Choices */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-lg hover:border-gray-400 transition-colors cursor-pointer">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Choice 1</h3>
                <p className="text-gray-700">{storyData?.choice1}</p>
              </div>
              
              <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-lg hover:border-gray-400 transition-colors cursor-pointer">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Choice 2</h3>
                <p className="text-gray-700">{storyData?.choice2}</p>
              </div>
              
              <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-lg hover:border-gray-400 transition-colors cursor-pointer">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Choice 3</h3>
                <p className="text-gray-700">{storyData?.choice3}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
    </div>
  );
};

export default PromptLife2;